import { BookOpen, Lightbulb, MonitorPlay, UserRoundCheck } from "lucide-react";
import { SolutionGroupContent, SolutionItemContent } from "./types";

// Também aparece em Educação; o link vai direto para a página /parceiro-pedagogico.
export const pedagogicalSolutions: SolutionItemContent = {
  key: "pedagogical",
  slug: "solucoes-pedagogicas",
  title: "Soluções Pedagógicas",
  icon: Lightbulb,
  href: "/parceiro-pedagogico",
  tagline: "Tecnologia a serviço do projeto pedagógico, com laboratórios, conteúdos e metodologias para a sala de aula.",
  what: [
    "Soluções pedagógicas combinam equipamentos, plataformas, conteúdos digitais e metodologias para transformar a forma de ensinar e aprender. Incluem laboratórios móveis com Chromebooks, kits de robótica e programação, recursos de IA para professores e estudantes e materiais alinhados à BNCC.",
    "Mais do que entregar tecnologia, apoiamos a escola na integração entre hardware, software e prática docente: planejamos o uso em sala, formamos os educadores e acompanhamos os resultados, para que cada recurso contribua de fato para a aprendizagem.",
  ],
  concepts: ["Laboratórios", "Conteúdos BNCC", "Metodologias", "IA na educação"],
  benefits: [
    { title: "Aulas mais engajadoras", text: "Recursos interativos e metodologias ativas que colocam o estudante no centro." },
    { title: "Alinhamento à BNCC", text: "Conteúdos e atividades conectados às competências do currículo." },
    { title: "Tecnologia que funciona", text: "Equipamentos e plataformas integrados e prontos para a rotina da escola." },
    { title: "Professores apoiados", text: "Formação e acompanhamento para usar cada recurso com segurança." },
  ],
  steps: [
    { title: "Escuta da escola", text: "Entendemos o projeto pedagógico, a infraestrutura e os desafios de aprendizagem." },
    { title: "Proposta integrada", text: "Combinamos equipamentos, plataformas e conteúdos de acordo com cada etapa de ensino." },
    { title: "Implantação e formação", text: "Instalamos as soluções e preparamos professores e coordenadores para o uso em sala." },
    { title: "Acompanhamento pedagógico", text: "Visitas, mentorias e indicadores para medir o impacto e ajustar o caminho." },
  ],
  audience: ["Redes municipais e estaduais de ensino", "Escolas particulares", "Secretarias de educação", "Coordenações pedagógicas"],
};

export const smartLearning: SolutionGroupContent = {
  key: "smart_learning",
  slug: "smart-learning",
  title: "Smart Learning",
  titleKey: "servicos.items.smart_learning.title",
  icon: BookOpen,
  tagline: "Aprendizagem acessível, interativa e inspiradora, com plataformas e práticas que acompanham o estudante.",
  description: [
    "O Smart Learning reúne as plataformas e as metodologias que tornam o ensino mais digital e personalizado: ambientes virtuais de aprendizagem, gestão da jornada acadêmica e soluções pedagógicas que levam tecnologia, laboratórios e conteúdos para a sala de aula.",
    "Cuidamos de toda a cadeia, da escolha e implantação das ferramentas à integração com os sistemas que a instituição já usa e à formação de quem vai utilizá-las, para que a tecnologia sirva ao projeto pedagógico e não o contrário.",
  ],
  concepts: ["Plataformas", "Conteúdos", "Jornada do aluno", "Práticas"],
  items: [
    {
      key: "moodle",
      slug: "ambientes-virtuais-moodle",
      title: "Ambientes Virtuais (Moodle)",
      icon: MonitorPlay,
      tagline: "Um ambiente virtual de aprendizagem robusto, com a identidade da instituição e pronto para escalar.",
      what: [
        "O Moodle é a plataforma de código aberto de ensino a distância mais usada no mundo e no Brasil. Nele a instituição organiza cursos, aulas, materiais, fóruns, questionários, tarefas e notas, para turmas presenciais, híbridas ou totalmente on-line, com trilhas, certificados e relatórios de acompanhamento.",
        "Hospedamos o ambiente em Google Cloud com alta disponibilidade, personalizamos a aparência, integramos com Google Workspace, sistemas acadêmicos e login único, e mantemos tudo atualizado e seguro, para que a equipe pedagógica se preocupe só com os cursos.",
      ],
      concepts: ["Cursos", "Avaliações", "Certificados", "Relatórios"],
      benefits: [
        { title: "Ensino em qualquer formato", text: "Turmas presenciais, híbridas ou a distância no mesmo ambiente." },
        { title: "Escala sem quedas", text: "Infraestrutura em nuvem que aguenta picos de acesso em provas e matrículas." },
        { title: "Integração", text: "Login único e conexão com Google Workspace e com o sistema acadêmico." },
        { title: "Acompanhamento", text: "Relatórios de participação e desempenho para agir cedo com cada turma." },
      ],
      steps: [
        { title: "Planejamento", text: "Entendemos o modelo de ensino, o volume de usuários e as integrações necessárias." },
        { title: "Implantação em nuvem", text: "Instalamos o Moodle em Google Cloud com backup, segurança e escalabilidade." },
        { title: "Personalização", text: "Identidade visual, plugins, estrutura de cursos, perfis e integrações configurados." },
        { title: "Sustentação", text: "Atualizações, monitoramento, suporte e formação de administradores e tutores." },
      ],
      audience: ["Universidades e institutos federais", "Escolas de governo e de formação de servidores", "Redes de ensino com EAD ou ensino híbrido", "Instituições de cursos livres e técnicos"],
    },
    {
      key: "slm",
      slug: "student-lifecycle-management",
      title: "Student Lifecycle Management",
      icon: UserRoundCheck,
      tagline: "A jornada completa do estudante, da captação à formatura, acompanhada com dados em um só lugar.",
      what: [
        "Student Lifecycle Management (gestão do ciclo de vida do estudante) é a abordagem que conecta todas as etapas da relação com o aluno: interesse, inscrição, matrícula, acompanhamento acadêmico, permanência, formatura e relacionamento como egresso. Em vez de dados espalhados em vários sistemas, a instituição passa a ter uma visão única de cada estudante.",
        "Com integração de dados e painéis em Google Cloud, é possível identificar sinais de risco de evasão, personalizar a comunicação, automatizar processos da secretaria e medir o impacto de cada ação. O resultado é mais permanência, mais eficiência e decisões baseadas em evidências.",
      ],
      concepts: ["Captação", "Matrícula", "Permanência", "Egressos"],
      benefits: [
        { title: "Menos evasão", text: "Alertas de risco com base em frequência, notas e engajamento para agir a tempo." },
        { title: "Visão 360° do aluno", text: "Dados acadêmicos, financeiros e de relacionamento reunidos em um só lugar." },
        { title: "Processos automatizados", text: "Inscrições, matrículas e comunicações com menos papel e menos retrabalho." },
        { title: "Gestão por dados", text: "Painéis com indicadores de cada etapa da jornada para orientar decisões." },
      ],
      steps: [
        { title: "Mapeamento da jornada", text: "Desenhamos as etapas, os pontos de contato e os sistemas envolvidos." },
        { title: "Integração de dados", text: "Conectamos sistema acadêmico, AVA, CRM e outras fontes em uma base unificada." },
        { title: "Painéis e alertas", text: "Criamos indicadores, modelos de risco de evasão e automações de comunicação." },
        { title: "Evolução", text: "Acompanhamos os resultados e ajustamos as ações com as equipes acadêmicas." },
      ],
      audience: ["Universidades e faculdades", "Institutos federais", "Secretarias de educação", "Escolas técnicas e de idiomas"],
    },
    pedagogicalSolutions,
  ],
};
